import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LocalOffer,
  Navigation,
  LocationOn,
  Store,
  DeleteOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { url } from '../../constants';

// ✅ Helper to fix the missing image path and link it to the backend server
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('data:image') || imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${url}${imagePath}`;
};

const VendorDeals = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [vendor, setVendor] = useState(null);
  const [categories, setCategories] = useState([]);
  const [removingId, setRemovingId] = useState(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Load categories from localStorage
  useEffect(() => {
    const savedCategories = localStorage.getItem('categories');
    if (savedCategories) {
      try {
        const parsedCategories = JSON.parse(savedCategories);
        if (Array.isArray(parsedCategories) && parsedCategories.length > 0) {
          setCategories(parsedCategories);
        }
      } catch (error) {
        console.error('Error parsing categories:', error);
      }
    }
  }, []);

  useEffect(() => {
    try {
      const storedVendor = localStorage.getItem('vendor_data');
      const token = localStorage.getItem('vendor_token');
      if (!storedVendor || !token) {
        setApiError('No vendor session found. Please login again.');
        setLoading(false);
        return;
      }
      const v = JSON.parse(storedVendor);
      setVendor(v);
    } catch {
      setApiError('Invalid vendor session. Please login again.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchDeals = async () => {
      if (!vendor) return;
      setLoading(true);
      setApiError('');

      try {
        const token = localStorage.getItem('vendor_token');
        const res = await fetch(`${url}/vendors/me/deals`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.detail || 'Failed to fetch deals');
        }
        setDeals(data);
      } catch (err) {
        setApiError(err.message || 'Failed to fetch deals');
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, [vendor]);

  const handleRemoveDeal = async (dealId) => {
    setRemovingId(dealId);

    try {
      const token = localStorage.getItem('vendor_token');

      const response = await fetch(`${url}/vendors/me/deals/${dealId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete deal');
      }

      // Remove from local state
      setDeals(prev => prev.filter(deal => deal._id !== dealId && deal.id !== dealId));
      
      console.log('✅ Deal deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting deal:', error);
      setApiError('Failed to delete deal');
    } finally {
      setRemovingId(null);
    }
  };

  const getCategoryColor = (categoryName) => {
    const savedCategory = categories.find(cat => 
      cat.cate_name === categoryName || 
      categoryName.includes(cat.cate_name) ||
      cat.cate_name.includes(categoryName)
    );
    
    if (savedCategory && savedCategory.cate_color) {
      return savedCategory.cate_color;
    }

    const colorMap = {
      'Electronics': '#FF6B6B',
      'Fashion': '#4ECDC4',
      'Food': '#45B7D1',
      'Grocery': '#96CEB4',
      'Travel': '#FECA57',
      'Home': '#FF9FF3',
      'Beauty': '#54A0FF',
      'Books': '#5F27CD',
      'Sports': '#00D2D3',
      'Groceries': '#1a7f3a',
      'Food & Dining': '#FF6B6B',
      'Shopping & Retail': '#4ECDC4',
    };
    
    return colorMap[categoryName] || '#1a7f3a';
  };

  const getDarkCategoryChipColor = (categoryName) => {
    const color = getCategoryColor(categoryName);
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const darkerR = Math.max(0, Math.floor(r * 0.6));
    const darkerG = Math.max(0, Math.floor(g * 0.6));
    const darkerB = Math.max(0, Math.floor(b * 0.6));
    
    return `rgb(${darkerR}, ${darkerG}, ${darkerB})`;
  };

  const formatPrice = (value) =>
    typeof value === 'number' ? `₹${value.toFixed(0)}` : 'N/A';

  const getDiscountPercent = (deal) => {
    const p1 = deal.percentage_discount;
    const p2 = deal.discount_percentage;
    if (p1 != null) return p1;
    if (p2 != null) return p2;
    if (deal.original_price && deal.discounted_price) {
      return Math.round(
        ((deal.original_price - deal.discounted_price) / deal.original_price) * 100
      );
    }
    return null;
  };

  const getDistanceLabel = (deal) => {
    if (deal.distance != null) {
      return `${deal.distance.toFixed(1)} km`;
    }
    return deal.city || 'Nearby';
  };

  if (!vendor && !loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/vendor/login')}
            >
              Login
            </Button>
          }
        >
          {apiError || 'No vendor logged in.'}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 6 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#1a7f3a' }} />
            <Typography sx={{ mt: 2 }}>Loading your deals...</Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: isMobile ? 2 : 6 }}>
      <Container maxWidth="lg" sx={{ px: isMobile ? 0 : 2 }}>
        {/* Header */}
        <Box sx={{ mb: isMobile ? 2 : 3, px: isMobile ? 2 : 0 }}>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            fontWeight={700}
            sx={{ color: '#4B5563', mb: 1 }}
          >
            My Deals
          </Typography>
          <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
            Showing all deals from {vendor?.business_name || 'your store'}
          </Typography>
        </Box>

        {/* Add New Deal button */}
        <Box
          sx={{
            mb: isMobile ? 2 : 3,
            px: isMobile ? 2 : 0,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            size={isMobile ? 'medium' : 'large'}
            onClick={() => navigate('/vendor/add_deals')}
            startIcon={<LocalOffer />}
            sx={{
              textTransform: 'none',
              borderRadius: 999,
              bgcolor: '#1a7f3a',
              '&:hover': { bgcolor: '#0f5328' },
            }}
          >
            Add New Deal
          </Button>
        </Box>

        {apiError && (
          <Box sx={{ mb: 2, px: isMobile ? 2 : 0 }}>
            <Alert severity="error" onClose={() => setApiError('')}>
              {apiError}
            </Alert>
          </Box>
        )}

        {!apiError && deals.length === 0 && (
          <Box sx={{ px: isMobile ? 2 : 0, py: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No deals found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You have not posted any deals yet. Start by creating your first deal.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/vendor/add_deals')}
              startIcon={<LocalOffer />}
              sx={{ bgcolor: '#1a7f3a', '&:hover': { bgcolor: '#0f5328' } }}
            >
              Create Deal
            </Button>
          </Box>
        )}

        {deals.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: isMobile ? 0 : 2,
              mb: 4,
            }}
          >
            {deals.map((deal, index) => {
              const discountPercent = getDiscountPercent(deal);
              const dealId = deal._id || deal.id;
              const dealDarkChipColor = getDarkCategoryChipColor(deal.category);
              const isRemoving = removingId === dealId;

              // Safely get image URL
              const rawImageUrl = deal.deal_url || deal.image_url || deal.img_url;
              const finalImageUrl = rawImageUrl 
                ? getImageUrl(rawImageUrl) 
                : 'https://via.placeholder.com/400x300?text=No+Image';

              return (
                <Card
                  key={dealId}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: isMobile ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                    borderRadius: isMobile ? 0 : 1,
                    border: isMobile ? '2px solid #E5E7EB' : 'none',
                    borderRight: isMobile ? '2px solid #E5E7EB' : 'none',
                    borderBottom: isMobile ? '2px solid #E5E7EB' : 'none',
                    overflow: 'hidden',
                    // Optional grayscale effect if the deal is paused
                    filter: deal.is_active ? 'none' : 'grayscale(30%)', 
                    opacity: deal.is_active ? 1 : 0.85,
                    '&:hover': {
                      boxShadow: isMobile ? 'none' : '0 4px 12px rgba(0,0,0,0.15)',
                      transform: isMobile ? 'none' : 'translateY(-4px)',
                      filter: 'none',
                      opacity: 1
                    },
                  }}
                  onClick={() => window.open(`/deal/${dealId}`, '_blank')}
                >
                  {/* Image Container */}
                  <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      height={isMobile ? "140" : "180"}
                      image={finalImageUrl}
                      alt={deal.deal_name}
                      sx={{
                        objectFit: 'cover',
                        userSelect: 'none',
                        pointerEvents: 'none',
                      }}
                    />

                    {/* ✅ STATUS CHIP (Active/Paused) - Top Left */}
                    <Chip
                      label={deal.is_active ? 'Active' : 'Paused'}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: isMobile ? 8 : 12,
                        left: isMobile ? 8 : 12,
                        fontWeight: 700,
                        bgcolor: deal.is_active ? '#10B981' : '#F59E0B',
                        color: 'white',
                        fontSize: isMobile ? '0.65rem' : '0.7rem',
                        height: isMobile ? 20 : 24,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        zIndex: 3,
                      }}
                    />

                    {/* ✅ DELETE BUTTON - Top Right */}
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveDeal(dealId);
                      }}
                      disabled={isRemoving}
                      sx={{
                        position: 'absolute',
                        top: isMobile ? 4 : 8,
                        right: isMobile ? 4 : 8,
                        zIndex: 10,
                        bgcolor: 'rgba(255,255,255,0.9)',
                        width: isMobile ? 28 : 32,
                        height: isMobile ? 28 : 32,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        '&:hover': {
                          bgcolor: '#FFE5E5',
                          color: '#FF3E3E',
                        },
                      }}
                    >
                      {isRemoving ? (
                        <CircularProgress size={isMobile ? 16 : 18} sx={{ color: '#FF3E3E' }} />
                      ) : (
                        <DeleteOutline fontSize="small" sx={{ fontSize: isMobile ? 16 : 18, color: '#4B5563' }} />
                      )}
                    </IconButton>

                    {/* ✅ DISTANCE CHIP - Bottom Left */}
                    {deal.distance !== undefined && deal.distance !== null && (
                      <Chip
                        icon={<Navigation />}
                        label={`${deal.distance} km`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: isMobile ? 8 : 12,
                          left: isMobile ? 8 : 12,
                          fontWeight: 700,
                          bgcolor: 'rgba(255,255,255,0.95)',
                          color: 'black',
                          fontSize: isMobile ? '0.65rem' : '0.7rem',
                          height: isMobile ? 20 : 24,
                          backdropFilter: 'blur(10px)',
                          zIndex: 3,
                          '& .MuiChip-icon': {
                            color: 'black',
                          },
                        }}
                      />
                    )}
                    
                    {/* ✅ RED DISCOUNT CHIP - Bottom Right */}
                    {discountPercent != null && (
                      <Chip
                        label={`${discountPercent}% OFF`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: isMobile ? 8 : 12,
                          right: isMobile ? 8 : 12,
                          fontWeight: 700,
                          bgcolor: '#FF4444',
                          color: 'white',
                          fontSize: isMobile ? '0.65rem' : '0.7rem',
                          height: isMobile ? 20 : 24,
                          boxShadow: '0 4px 12px rgba(255,68,68,0.5)',
                          zIndex: 3,
                        }}
                      />
                    )}
                  </Box>

                  <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: isMobile ? 0.5 : 1 }}>
                      {deal.category && (
                        <Chip
                          label={deal.category}
                          size="small"
                          sx={{
                            bgcolor: dealDarkChipColor,
                            color: 'white',
                            fontWeight: 700,
                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                            height: isMobile ? 18 : 24,
                            width: '100%',
                            justifyContent: 'center',
                          }}
                        />
                      )}
                    </Stack>

                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: '#212121',
                        fontSize: isMobile ? '0.8rem' : '0.95rem',
                      }}
                    >
                      {deal.deal_name}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{ mb: 0.5 }}
                    >
                      <Store sx={{ fontSize: 12, color: '#878787' }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#878787',
                          fontSize: isMobile ? '0.6rem' : '0.7rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {deal.vendor_name || vendor?.business_name}
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="baseline"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                          color: '#1a7f3a',
                          fontSize: isMobile ? '0.85rem' : '1rem',
                        }}
                      >
                        {deal.discounted_price
                          ? formatPrice(deal.discounted_price)
                          : formatPrice(deal.original_price)}
                      </Typography>
                      {deal.original_price && deal.discounted_price && (
                        <Typography
                          variant="caption"
                          sx={{
                            textDecoration: 'line-through',
                            color: '#878787',
                            fontSize: isMobile ? '0.65rem' : '0.75rem',
                          }}
                        >
                          {formatPrice(deal.original_price)}
                        </Typography>
                      )}
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{ mb: isMobile ? 1 : 1.5 }}
                    >
                      <LocationOn sx={{ fontSize: 12, color: '#1a7f3a' }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#878787',
                          fontSize: isMobile ? '0.6rem' : '0.7rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getDistanceLabel(deal)}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/deal/${dealId}`, { state: { deal } });
                        }}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 999,
                          fontSize: isMobile ? '0.65rem' : '0.8rem',
                          py: isMobile ? 0.4 : 0.75,
                          borderColor: '#1a7f3a',
                          color: '#1a7f3a',
                          '&:hover': {
                            borderColor: '#1a7f3a',
                            bgcolor: '#E8F5E9',
                          },
                        }}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/vendor/deals/${dealId}/edit`, {
                            state: { deal },
                          });
                        }}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 999,
                          fontSize: isMobile ? '0.65rem' : '0.8rem',
                          py: isMobile ? 0.4 : 0.75,
                          bgcolor: '#1a7f3a',
                          '&:hover': {
                            bgcolor: '#0f5328',
                          },
                        }}
                      >
                        Edit
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default VendorDeals;